<!--
  Eigenständiges Widget: zeigt eine Story-Row (siehe BetmanStoryCard). Initialisierung macht NUR
  EINEN Server-Aufruf (der Story-Endpunkt) - KEIN kompletter Spielplan-Request beim Start.

  Beim Öffnen einer Story (egal welche) wird GENAU DIESES eine Match nachgeladen (lib/
  matchLookup.js: ladeEinzelnesMatch) - ebenfalls nur EIN Request pro Match, gecacht, falls
  dieselbe Story mehrfach geöffnet wird. Die Tabelle wird NICHT hier geladen - das übernimmt
  DetailOverlay selbst lazy, sobald der "Tabelle"-Tab tatsächlich geöffnet wird (siehe dort:
  ladeTabelleFuerLiga, gecacht nach Liga statt nach Match).

  Öffnet dann das generische DetailOverlay (alle Tabs: Vorschau/Spielbericht/Aufstellung/Events/
  Wechsel/Statistik/Tabelle), je nachdem was fürs Match tatsächlich vorliegt.
-->
<template>
  <div class="ts-app">
    <Transition name="story-row-fade">
      <div v-if="!loading && zeigeReiheAktiv" ref="storyRowRef" class="story-row">
        <!-- Trigger für die Vollbild-Übersicht - IMMER sichtbar, unabhängig vom Zustand der
             Reihe (Fehler/leer/Karten) - sonst verschwindet er z.B. komplett, wenn die
             Reihe selbst gerade keine Stories hat, obwohl die Übersicht ja einen eigenen,
             unabhängig wählbaren Zeitraum hat und trotzdem was zeigen könnte. -->
      <div class="story-row__alle-wrapper">
        <button type="button" class="story-row__alle-btn" @click="uebersichtOeffnen">
          <span class="story-row__alle-icon">☰</span>
          <span class="story-row__alle-label">Alle</span>
        </button>
      </div>

      <div v-if="error" class="tm-state tm-state--column tm-state--error">
        {{ error }}
        <button type="button" class="tm-retry-btn" @click="ladeStories">Erneut versuchen</button>
      </div>

      <div v-else-if="stories.length === 0" class="tm-state">
        Aktuell keine Stories verfügbar.
      </div>

      <template v-else>
        <div class="story-row--card-container">
          <div v-for="story in storyCards" :key="story.key" :ref="el => registriereStoryKarte(story.key, el)">
            <BetmanStoryCard
              :match-label="story.matchLabel"
              :is-new="story.isNew"
              :phase-label="story.phaseLabel"
              :phase="story.phase"
              :info-text="story.infoText"
              :is-live="story.istLive"
              :flagge-bild="story.flaggeBild"
              :flagge-groesse="story.flaggeGroesse"
              @open="oeffneStory(story)"
            />
          </div>
        </div>
      </template>
      </div>
    </Transition>

    <Teleport :to="portalZiel">
      <Transition name="tm-sheet-fade">
        <div v-if="offenesDetail || ladeMatchLaeuft" class="tm-sheet-backdrop" @click="schliesseDetail"></div>
      </Transition>
      <Transition name="tm-sheet-slide">
        <div v-if="ladeMatchLaeuft" class="tm-detail-overlay tm-detail-overlay--schmal">
          <div class="tm-state"><span class="tm-state__spinner"></span> Lade Match...</div>
        </div>
        <!-- GEÄNDERT: Ein Overlay für beide Fälle - offenesDetail ist entweder {match, tab}
             (match-gebundene Anzeige) oder {story} (matchlose Story). DetailOverlay verzweigt
             intern selbst (istStandaloneStory) - kein separates BetmanStoryDetail.vue mehr
             nötig, eine Overlay-Hülle statt zwei parallel gepflegter. -->
        <DetailOverlay
          v-else-if="offenesDetail"
          :detail="offenesDetail"
          :api-base="props.apiBase"
          @close="schliesseDetail"
          @change-tab="wechsleTab"
        />
      </Transition>

      <!-- Vollbild-Übersicht aller Stories - eigenständiges Overlay, nicht das Bottom-Sheet-
           Muster (DetailOverlay), da hier eine LISTE (nicht ein einzelnes Match) gezeigt wird. -->
      <Transition name="tm-sheet-fade">
        <div v-if="uebersichtOffen" class="betman-uebersicht">
          <div class="betman-uebersicht__kopf">
            <span class="betman-uebersicht__titel">Alle Stories</span>
            <a
              href="https://t.me/ike_teammatch"
              target="_blank"
              rel="noopener"
              class="betman-uebersicht__telegram-btn"
            >📢 Telegram</a>
            <button type="button" class="betman-uebersicht__schliessen" aria-label="Schließen" @click="uebersichtOffen = false">✕</button>
          </div>

          <!-- GEÄNDERT: Wochennavigation durch TAGESnavigation ersetzt - Nutzer wollte
               tageweise statt wochenweise durchblättern. "Aktuell"-Button bleibt (zeigt
               weiterhin ab gestern in die Zukunft), Rest ist jetzt Tag für Tag mit ‹ ›. Jeder
               Tag nutzt denselben geteilten Cache wie zuvor (ladeStoriesFuerZeitraum in
               useBetmanStories.js) - einmal geladene Tage werden beim erneuten Anwählen NICHT
               nochmal vom Server geholt. -->
          <div v-if="!uebersichtSuche.trim()" class="betman-uebersicht__wochen-nav">
            <button type="button" class="betman-uebersicht__wochen-btn" @click="uebersichtTagZurueck" aria-label="Vorheriger Tag">‹</button>
            <span class="betman-uebersicht__wochen-label">{{ uebersichtTagLabel }}</span>
            <button type="button" class="betman-uebersicht__wochen-btn" @click="uebersichtTagVor" aria-label="Nächster Tag">›</button>
            <button type="button" class="betman-uebersicht__aktuell-btn" @click="ladeAktuell">Aktuell</button>
          </div>

          <!-- Suche durchsucht Titel/Bericht-Text/Mannschaftsnamen gleichzeitig (siehe
               sucheText in baueStoryCards). GEÄNDERT: Sobald gesucht wird, gilt AKTUELL keine
               zeitliche Einschränkung mehr (weiter Zeitraum wird automatisch geladen, siehe
               watch(uebersichtSuche) unten) - die Wochennavigation wird währenddessen
               ausgeblendet, stattdessen optionale von/bis-Platzhalter-Felder für später (noch
               nicht ausgewertet, nur vorbereitet). -->
          <div class="betman-uebersicht__suche">
            <input
              v-model="uebersichtSuche"
              type="text"
              placeholder="Suchen (Team, Titel, Text) …"
              class="betman-uebersicht__suche-input"
            />
            <div v-if="uebersichtSuche.trim()" class="betman-uebersicht__suche-zeitraum">
              <input v-model="uebersichtSucheVon" type="text" placeholder="von 2026-1-1" class="betman-uebersicht__suche-zeitraum-input" />
              <input v-model="uebersichtSucheBis" type="text" placeholder="bis 2026-1-1" class="betman-uebersicht__suche-zeitraum-input" />
            </div>
          </div>

          <div v-if="uebersichtLoading" class="tm-state"><span class="tm-state__spinner"></span> Lade Stories...</div>
          <div v-else-if="uebersichtError" class="tm-state tm-state--error">{{ uebersichtError }}</div>
          <div v-else-if="uebersichtStoryCardsGefiltert.length === 0" class="tm-state">Keine Stories gefunden.</div>
          <div v-else class="betman-uebersicht__liste-scroll">
            <div class="betman-uebersicht__liste">
              <BetmanStoryCardErweitert
                v-for="story in uebersichtStoryCardsGefiltert"
                :key="'uebersicht-' + story.key"
                :match-label="story.matchLabel"
                :is-new="story.isNew"
                :phase-label="story.phaseLabel"
            :phase="story.phase"
                :info-text="story.infoText"
                :is-live="story.istLive"
                :flagge-bild="story.flaggeBild"
                :flagge-groesse="story.flaggeGroesse"
                :created-at="story.createdAt"
                :image-url="story.imageUrl"
              :seiten-url="story.seitenUrl"
              :liga="story.liga"
                @open="oeffneAusUebersicht(story)"
              />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import BetmanStoryCard from './BetmanStoryCard.vue'
import BetmanStoryCardErweitert from './BetmanStoryCardErweitert.vue'
import DetailOverlay from './DetailOverlay.vue'
import { useGeseheneStories } from '../composables/useGeseheneStories.js'
import { useBetmanStories, ladeStoriesFuerZeitraum, heutePlusTage } from '../composables/useBetmanStories.js'
import { flaggenGroesse } from '../lib/ligaTypen.js'
import { getPortalTarget } from '../lib/portal.js'
import { parseStoryJson, sollTippZeigen } from '../lib/story.js'
import { ladeEinzelnesMatch } from '../lib/matchLookup.js'

const portalZiel = getPortalTarget()

const props = defineProps({
  apiBase: { type: String, default: '/rest/courseservice/oddsservice/free' },
  // Basis-URL für relative Bild-Pfade (imageUrl/avatarUrl aus der Story-JSON) - Default die
  // Produktions-Domain, lokal beim Testen z.B. auf "" setzen (dann bleiben Pfade relativ, falls
  // der lokale Dev-Proxy/Server das schon selbst korrekt auflöst).
  bildBasisUrl: { type: String, default: 'https://app.team-match.io' },
  // Datumsbereich NUR für den Story-Endpunkt - Format 'YYYY-MM-DD'. Defaults: -1 bis +90 Tage.
  von: { type: String, default: null },
  bis: { type: String, default: null },
  // NEU: steuert, was beim Klick auf eine Story-Karte passiert - 'overlay' (Standard) öffnet
  // das interne Detail-Overlay wie bisher; 'statische-seite' navigiert stattdessen direkt zur
  // statischen Betman-Seite (story.seitenUrl, neuer Tab). Für externe Einbettungen auf anderen
  // Seiten gedacht, die kein eigenes Overlay-System wollen/brauchen, sondern einfach auf die
  // fertige, eigenständige Story-Seite verlinken sollen.
  klickVerhalten: { type: String, default: 'overlay' }, // 'overlay' | 'statische-seite'
  zeigeReihe: { type: Boolean, default: true }
})

defineExpose({
  uebersichtOeffnen
})

const { istGesehen, markiereAlsGesehen } = useGeseheneStories()

// Story-Row-Daten (Bild/Titel/isNew) UND die Story-INHALTE selbst (titel/bericht/quoten/tipp/
// createdAt/timeMatch) kommen komplett aus diesem EINEN Endpunkt. GEÄNDERT: jetzt über eine
// geteilte Composable (useBetmanStories) statt lokaler Refs + eigener Fetch-Logik - falls das
// Widget auf mehreren Seiten gleichzeitig läuft (z.B. Startseite UND Tipp-Seite), wird der
// Story-Endpunkt dadurch nur EINMAL aufgerufen, nicht einmal pro Seite/Instanz.
const { stories, loading, error, ladeStories } = useBetmanStories(props)

// Gecachte, bereits geladene Einzel-Matches (matchId -> Match-Objekt) - verhindert erneutes
// Laden, falls dieselbe Story mehrfach geöffnet wird.
const geladeneMatches = new Map()
const ladeMatchLaeuft = ref(false)
const ladeMatchFehler = ref(null)

// Fürs Rendering in BetmanStoryCard aufbereitet - JEDE Story ist jetzt eine EIGENE Karte (auch
// wenn zwei Stories zum selben Match gehören, z.B. Vorschau UND Bericht gleichzeitig verfügbar).
// key/phase eindeutig aus matchId+status abgeleitet, damit Vue-Keys nicht kollidieren und
// oeffneStory später weiß, welchen Tab es öffnen soll, statt das aus dem Match-Status zu raten.
const WOCHENTAGE_KURZ = ['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA']

// Formatiert timeMatch (z.B. "30, Aug. 2026 19:00") zu einer kurzen Form wie "SO 19:00". Das
// Format vom Server ist ungewöhnlich (kein sauberes ISO) - falls new Date() das nicht parsen
// kann, wird der Rohwert einfach unverändert gezeigt statt gar nichts.
function formatiereKurzzeit(datumString) {
  if (!datumString) return ''
  const d = new Date(datumString)
  if (Number.isNaN(d.getTime())) return datumString
  const pad = (n) => String(n).padStart(2, '0')
  return `${WOCHENTAGE_KURZ[d.getDay()]} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Live: scoresLive vorhanden, Spiel nicht als beendet markiert, UND tatsächlich schon eine
// Spielzeit vergangen (elapsed > 0) mit gesetztem Kurz-Status (shortS, z.B. "1H"/"2H"/"HT") -
// verhindert, dass ein Match fälschlich als "live" gilt, nur weil scoresLive technisch existiert
// (z.B. kurz vor Anpfiff, wenn der Datensatz schon angelegt aber elapsed noch 0 ist).
function istLive(story) {
  const status = story.scoresLive?.status
  return story.scoresLive != null
    && story.completed !== true
    && status?.elapsed > -1
    && !!status?.shortS
}

// Extrahiert, damit sowohl die Reihe (storyCards) als auch die Übersicht (uebersichtStoryCards,
// mit eigenem, unabhängig wählbarem Zeitraum) dieselbe Aufbereitungslogik nutzen, statt sie zu
// duplizieren.
// Deutsche Anzeige-Labels je Phase - erweiterbar für künftige Kategorien (siehe Notiz:
// Fakten/Skandal/Transfer sollen später dazukommen). Unbekannte/fehlende phase-Werte fallen
// auf 'preview' zurück (siehe PHASE_LABELS[phase] ?? PHASE_LABELS.preview unten).
const PHASE_LABELS = {
  preview: 'Vorschau',
  report: 'Bericht',
  story: 'Story',
  rumor: 'Gerücht'
}

function baueStoryCards(rohStories) {
  return rohStories
    .map(s => {
      const geparst = parseStoryJson(s, props.bildBasisUrl)
      // GEÄNDERT: statt binärem report/preview-Vergleich jetzt gegen die bekannten
      // PHASE_LABELS geprüft - unbekannte Werte fallen auf 'preview' zurück, statt z.B. mit
      // undefined weiterzulaufen.
      const phase = PHASE_LABELS[s.phase] ? s.phase : 'preview'
      const key = `${s.id}-${phase}`
      // NEU: matchId kann fehlen (Transfer/Verletzung/Gerücht etc. - eigenständige Story ohne
      // Spielbezug). hatMatch steuert sowohl die Anzeige (kein "vs"-Matchup, andere infoText-
      // Logik) als auch das spätere Öffnen (oeffneStory lädt dann KEIN Match).
      // GEÄNDERT: matchId === 0 wird vom Backend offenbar auch als "kein Match" genutzt (nicht
      // nur null/undefined) - "!= null" allein reichte nicht, da 0 davon NICHT erfasst wird
      // (0 != null ist true in JS!). Dadurch versuchte oeffneStory, Match "0" zu laden, was
      // fehlschlug - das Overlay ging kurz auf (ladeMatchLaeuft-Backdrop) und gleich wieder zu
      // (Fehler-Pfad setzt offenesDetail ja nie).
      const hatMatch = s.matchId != null && s.matchId !== 0
      const infoText = !hatMatch
        ? '' // keine Anstoßzeit/Ergebnis bei matchlosen Storys - Phase-Badge zeigt ja schon die Kategorie
        : (s.completed ? (s.result ?? '') : formatiereKurzzeit(s.timeMatch))
      const matchLabel = (s.heim && s.auswaerts)
        ? `${s.heim} - ${s.auswaerts}`
        : (geparst?.titel ?? (hatMatch ? `Match ${s.id}` : `Story ${s.id}`))
      return {
        key,
        id: s.id,
        matchId: s.matchId,
        hatMatch,
        phase,
        phaseLabel: PHASE_LABELS[phase],
        infoText,
        istLive: hatMatch && istLive(s),
        flaggeBild: s.flaggeBild,
        flaggeGroesse: flaggenGroesse(s.ligaId),
        matchLabel,
        isNew: !istGesehen(key),
        // GEÄNDERT: Sortier-Wert jetzt auf updatedAt (zuletzt aktualisiert) statt createdAt -
        // "aktuellste Story zuerst" bedeutet jetzt: zuletzt bearbeitet/aktualisiert. Fallback auf
        // createdAt, falls updatedAt bei älteren/unvollständigen Datensätzen (noch) fehlt.
        sortierZeitstempel: new Date(s.updatedAt ?? s.createdAt).getTime(),
        // Fürs Durchsuchen der Übersicht - fasst Titel/Bericht-Text/Mannschaftsnamen in EINEM
        // kleingeschriebenen String zusammen, damit die Suche alles auf einmal abdeckt, statt
        // mehrere Felder einzeln prüfen zu müssen.
        sucheText: [matchLabel, geparst?.titel, geparst?.bericht, s.heim, s.auswaerts]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
        createdAt: s.createdAt,
        imageUrl: geparst?.imageUrl ?? null,
        seitenUrl: geparst?.url ?? null, // Link zur statischen Betman-Seite, zum Teilen
        liga: s.liga
      }
    })
    .sort((a, b) => {
      const aZeit = Number.isNaN(a.sortierZeitstempel) ? -Infinity : a.sortierZeitstempel
      const bZeit = Number.isNaN(b.sortierZeitstempel) ? -Infinity : b.sortierZeitstempel
      return bZeit - aZeit // absteigend - neueste zuerst
    })
}

const storyCards = computed(() => baueStoryCards(stories.value))
const zeigeReiheAktiv = computed(() => props.zeigeReihe !== false && props.zeigeReihe !== 'false')

// Suche in der Übersicht - filtert über den vorbereiteten sucheText (Titel+Bericht+Mannschaften)
const uebersichtSuche = ref('')
const uebersichtStoryCardsGefiltert = computed(() => {
  const suchbegriff = uebersichtSuche.value.trim().toLowerCase()
  if (!suchbegriff) return uebersichtStoryCards.value
  return uebersichtStoryCards.value.filter(s => s.sucheText.includes(suchbegriff))
})

const offenesDetail = ref(null)
const uebersichtOffen = ref(false)

// ---------- Übersicht: eigener, interaktiv wählbarer Zeitraum ----------
// Unabhängig vom fixen Zeitraum der Reihe (props.von/bis) - der Nutzer soll in der Übersicht
// auch auf ÄLTERE Stories zugreifen können, nicht nur den schmalen Reihen-Zeitraum.
const uebersichtStories = ref([])
const uebersichtLoading = ref(false)
const uebersichtError = ref(null)
const uebersichtStoryCards = computed(() => baueStoryCards(uebersichtStories.value))

// ---------- Tagesnavigation: 0 = heute, +1/-1 = ein Tag vor/zurück ----------
const uebersichtTagOffset = ref(0)

function alsIsoDatum(d) {
  return d.toISOString().slice(0, 10) // 'YYYY-MM-DD'
}

function tagMitOffset(offset) {
  const d = new Date()
  d.setDate(d.getDate() + offset)
  d.setHours(0, 0, 0, 0)
  return d
}

// GEÄNDERT: "Aktuell"-Modus zusätzlich zur Tagesnavigation - zeigt IMMER alles ab gestern in
// die (praktisch unbegrenzte) Zukunft, unabhängig vom Tages-Offset. Eigener State
// (istAktuellModus), damit uebersichtTagLabel weiß, ob gerade "Aktuell" oder ein bestimmter
// Tag angezeigt wird - beide Modi schließen sich gegenseitig aus.
const istAktuellModus = ref(false)

const uebersichtTagLabel = computed(() => {
  if (istAktuellModus.value) return 'Aktuell'
  if (uebersichtTagOffset.value === 0) return 'Heute'
  if (uebersichtTagOffset.value === 1) return 'Morgen'
  if (uebersichtTagOffset.value === -1) return 'Gestern'
  const tag = tagMitOffset(uebersichtTagOffset.value)
  return `${String(tag.getDate()).padStart(2, '0')}.${String(tag.getMonth() + 1).padStart(2, '0')}.`
})

async function ladeUebersicht(von, bis) {
  uebersichtLoading.value = true
  uebersichtError.value = null
  try {
    // GEÄNDERT: ladeStoriesFuerZeitraum cached selbst pro exaktem von/bis-Schlüssel (siehe
    // useBetmanStories.js: geteilterCache) - ein bereits geladener Tag wird beim erneuten
    // Anwählen also NICHT nochmal vom Server geholt, sondern kommt direkt aus dem Cache.
    const ergebnis = await ladeStoriesFuerZeitraum(props.apiBase, props.bildBasisUrl, von, bis)
    uebersichtStories.value = ergebnis.stories.value
    if (ergebnis.error.value) uebersichtError.value = ergebnis.error.value
  } catch (e) {
    uebersichtError.value = 'Stories konnten nicht geladen werden.'
    console.warn('ladeUebersicht fehlgeschlagen:', e)
  } finally {
    uebersichtLoading.value = false
  }
}

function ladeAktuellenTag() {
  const iso = alsIsoDatum(tagMitOffset(uebersichtTagOffset.value))
  ladeUebersicht(iso, iso)
}

// NEU: "Aktuell"-Button - ab gestern bis weit in die Zukunft (1 Jahr reicht als "unbegrenzt"
// völlig aus). Setzt den Tages-Offset NICHT zurück (bleibt im Hintergrund erhalten, falls der
// Nutzer später wieder zur Tagesnavigation wechselt).
function ladeAktuell() {
  istAktuellModus.value = true
  ladeUebersicht(heutePlusTage(-1), heutePlusTage(365))
}

function uebersichtTagZurueck() {
  istAktuellModus.value = false
  uebersichtTagOffset.value--
  ladeAktuellenTag()
}
function uebersichtTagVor() {
  istAktuellModus.value = false
  uebersichtTagOffset.value++
  ladeAktuellenTag()
}

// Beim ersten Öffnen der Übersicht den "Aktuell"-Modus (ab gestern in die Zukunft) einmalig
// laden - kein Grund, das schon vorab beim Widget-Start zu tun.
function uebersichtOeffnen() {
  uebersichtOffen.value = true
  if (uebersichtStories.value.length === 0 && !uebersichtLoading.value) {
    ladeAktuell()
  }
}

// ---------- Suche: aktuell KEINE zeitliche Einschränkung mehr, sobald gesucht wird ----------
// Platzhalter-Felder für von/bis (Format z.B. "2026-1-1") - noch nicht ausgewertet, nur als
// UI vorbereitet für später. Solange leer, wird bei aktiver Suche ein weiter Zeitraum geladen
// (1 Jahr zurück/voraus), praktisch "alles" für den üblichen Anwendungsfall.
const uebersichtSucheVon = ref('')
const uebersichtSucheBis = ref('')

watch(uebersichtSuche, (neu, alt) => {
  const jetztAktiv = neu.trim().length > 0
  const vorherAktiv = (alt ?? '').trim().length > 0
  if (jetztAktiv && !vorherAktiv) {
    ladeUebersicht(heutePlusTage(-365), heutePlusTage(365))
  } else if (!jetztAktiv && vorherAktiv) {
    // Suche wird geleert - zurück zum aktuell gewählten Tag
    ladeAktuellenTag()
  }
})

// GEÄNDERT: Übersicht bleibt jetzt beim Öffnen einer Story OFFEN (statt sie zu schließen und
// beim Detail-Schließen wieder zu öffnen) - vorher gab's zwischen "Übersicht zu" und "Detail
// tatsächlich sichtbar" (Lade-Zeit + Slide-Animation) eine kurze Lücke, in der man kurz die
// dahinterliegende Seite sah. Das Detail-Overlay legt sich jetzt einfach ÜBER die weiterhin
// offene Übersicht - kein Flackern mehr möglich, kein "Wieder-Öffnen" beim Schließen nötig.
function oeffneAusUebersicht(story) {
  oeffneStory(story)
}

// Reichert ein frisch geladenes Match-Objekt mit den Story-Daten an, die wir SCHON haben (aus
// stories.value) - kein zweiter Netzwerk-Request nötig, wir parsen einfach das bereits geladene
// Roh-Objekt, genau wie useSpielplanData.ladeStoriesFuerSichtbare es an anderer Stelle tut.
function reichereMitStoryAn(match, story) {
  const geparst = parseStoryJson(story, props.bildBasisUrl)
  //console.log('BETMAN geparst.url:', geparst?.url)
  if (!geparst) return

  const istPreview = story.phase === 'preview'
  const zeigeTipp = sollTippZeigen(story.phase)
  const angereichert = {
    ...geparst,
    heim: geparst.heim || match.heim,
    auswaerts: geparst.auswaerts || match.auswaerts,
    quote_1: story.quote_1,
    quote_X: story.quote_X,
    quote_2: story.quote_2,
    timeMatch: story.timeMatch,
    createdAt: story.createdAt,
    zeigeTipp
  }

  if (istPreview) {
    match.storyPreview = story.text
    match.storyDatenPreview = angereichert
  } else {
    match.storyReport = story.text
    match.storyDatenReport = angereichert
  }
}

async function oeffneStory(storyCard) {
  markiereAlsGesehen(storyCard.key) // pro Karte (Match+Phase bzw. Story-Kategorie), nicht nur pro Match

  // NEU: externe Einbettung - statt internem Overlay direkt zur statischen Betman-Seite
  // navigieren (neuer Tab). storyCard.seitenUrl kommt schon fertig aus baueStoryCards (gilt für
  // JEDE Karte, nicht nur die erweiterte) - kein erneutes Parsen/Laden nötig.
  if (props.klickVerhalten === 'statische-seite') {
    if (storyCard.seitenUrl) window.open(storyCard.seitenUrl, '_blank', 'noopener')
    return
  }

  // GEÄNDERT: Matchlose Story (kein storyCard.hatMatch) - kein Match zu laden, direkt die
  // geparsten Story-Daten ins EINE, gemeinsame DetailOverlay geben (detail = { story }, statt
  // { match, tab }) - DetailOverlay erkennt selbst, dass es sich um eine Standalone-Story
  // handelt (istStandaloneStory) und blendet Tabs entsprechend aus.
  if (!storyCard.hatMatch) {
    const rohStory = [...stories.value, ...uebersichtStories.value].find(s => s.id === storyCard.id)
    if (!rohStory) return
    const geparst = parseStoryJson(rohStory, props.bildBasisUrl)
    if (geparst) {
      geparst.heim = geparst.heim || rohStory.heim
      geparst.auswaerts = geparst.auswaerts || rohStory.auswaerts
      geparst.quote_1 = rohStory.quote_1
      geparst.quote_X = rohStory.quote_X
      geparst.quote_2 = rohStory.quote_2
      geparst.timeMatch = rohStory.timeMatch
      geparst.createdAt = rohStory.createdAt
      // War komplett vergessen worden - ohne das ist zeigeTipp einfach undefined,
      // BetmanStoryBody's v-if="story.zeigeTipp && ..." damit immer falsy. Betrifft genau die
      // Fälle (matchlose Storys), wo story/rumor/etc. am ehesten vorkommen.
      geparst.zeigeTipp = sollTippZeigen(storyCard.phase)
    }
    offenesDetail.value = { story: geparst }
    return
  }

  // Ab hier: match-gebundene Story (Vorschau/Bericht) - GEÄNDERT: geladeneMatches/
  // ladeEinzelnesMatch/reichereMitStoryAn-Filter laufen jetzt über storyCard.matchId statt
  // storyCard.id - id ist ja jetzt die eigene Story-ID (kann bei Preview/Bericht zum selben
  // Match unterschiedlich sein), matchId ist der eigentliche, gemeinsame Bezugspunkt.
  let match = geladeneMatches.get(storyCard.matchId)

  if (!match) {
    ladeMatchLaeuft.value = true
    ladeMatchFehler.value = null
    try {
      match = await ladeEinzelnesMatch(props.apiBase, storyCard.matchId)
      // ALLE zu diesem Match gehörenden Story-Einträge anreichern (nicht nur den ersten
      // gefundenen) - es kann ja gleichzeitig Preview UND Report für dasselbe Match geben.
      const zugehoerigeStories = [...stories.value, ...uebersichtStories.value].filter(s => s.matchId === storyCard.matchId)
      for (const story of zugehoerigeStories) {
        reichereMitStoryAn(match, story)
      }
      geladeneMatches.set(storyCard.matchId, match)
    } catch (e) {
      ladeMatchFehler.value = 'Match konnte nicht geladen werden.'
      console.warn('ladeEinzelnesMatch fehlgeschlagen:', e)
      ladeMatchLaeuft.value = false
      return
    }
    ladeMatchLaeuft.value = false
  }

  // GEÄNDERT: Tab kommt jetzt direkt von der geklickten Karte (storyCard.phase), nicht mehr aus
  // dem Match-Status geraten - wichtig, seit es zum selben Match zwei Karten geben kann
  // (Vorschau UND Bericht gleichzeitig), die jeweils gezielt ihren eigenen Tab öffnen sollen.
  const tab = storyCard.phase === 'preview' ? 'vorschau' : 'spielbericht'
  offenesDetail.value = { match, tab }
}
function schliesseDetail() {
  offenesDetail.value = null
}
function wechsleTab(tab) {
  if (offenesDetail.value) offenesDetail.value = { ...offenesDetail.value, tab }
}

// ---------- Auto-Scroll zur ersten NEUEN Story ----------
// Bei vielen Stories (Zeitraum jetzt bis zu 90 Tage) könnte die interessante (neue) Story sonst
// weit rechts außerhalb des sichtbaren Bereichs liegen - direkt beim Laden einmalig dorthin
// scrollen, statt dass der Nutzer erst selbst durchsuchen muss.
const storyRowRef = ref(null)
const storyKartenElemente = new Map() // story.key -> DOM-Element

function registriereStoryKarte(key, el) {
  if (el) storyKartenElemente.set(key, el)
  else storyKartenElemente.delete(key)
}

// GEÄNDERT: scrollIntoView allein reichte nicht mehr, seit der "Alle"-Button sticky ist - die
// Ziel-Karte konnte dabei exakt an den linken Rand rutschen und landete dann VISUELL hinter dem
// immer sichtbaren sticky-Button, obwohl programmatisch korrekt gescrollt wurde. Stattdessen
// manuell berechnen: Zielposition der Karte MINUS die Breite des sticky-Bereichs (Button-Wrapper
// + etwas Sicherheitsabstand), damit die Karte tatsächlich sichtbar direkt daneben landet.
function scrolleZurErstenNeuenStory() {
  const ziel = storyCards.value.find(s => s.isNew)
  if (!ziel) return
  const el = storyKartenElemente.get(ziel.key)
  const container = storyRowRef.value
  if (!el || !container) return

  const stickyBreite = container.querySelector('.story-row__alle-wrapper')?.offsetWidth ?? 0
  const zielPosition = el.offsetLeft - stickyBreite - 8 // 8px Sicherheitsabstand
  container.scrollTo({ left: Math.max(0, zielPosition), behavior: 'auto' })
}

onMounted(async () => {
  await ladeStories()
  // nextTick, damit die Ref-Wrapper (registriereStoryKarte) schon im DOM stehen, bevor gescrollt wird
  nextTick(() => scrolleZurErstenNeuenStory())
})
</script>